import * as THREE from 'three'
import { cover } from '../../utility/constant/helper.constant'
import { Helper } from './helper.class'
import { Status } from './status.class'
import { LogBar } from './logBar.class'
import {
  ITerraceMeasure,
  ITerraceRoof,
  ITerraceSidewall,
} from '../../utility/interface/terraceCover.interface'
import {
  TDockOpacity,
  TGlassOpacity,
} from '../../utility/type/terraceCover.type'
import { color } from '../../utility/constant/color.constant'

export abstract class WindowElement {
  /**
   * Shape of the side window
   * @param measure The measure context values
   * @returns The shape of the side window
   */
  public static shape(measure: ITerraceMeasure): THREE.Shape {
    const { height, deltaY } = this.shapePositions(measure)

    const windowShape = new THREE.Shape()
    windowShape.moveTo(0, 0)
    windowShape.moveTo(0, Helper.convertToMeter(deltaY))
    windowShape.lineTo(
      Helper.convertToMeter(measure.userDepth),
      Helper.convertToMeter(deltaY + height)
    )
    windowShape.lineTo(Helper.convertToMeter(measure.userDepth), 0)
    windowShape.lineTo(0, 0)

    return windowShape
  }

  /**
   * Height (height, deltaY) of the upper side window
   * @param measure The measure context values
   * @returns The height of the upper side window
   */
  public static shapePositions(measure: ITerraceMeasure): {
    height: number
    deltaY: number
  } {
    const deltaY = 3 //? value that makes it no perfect triangle
    const height =
      Math.tan((measure.userAngle * Math.PI) / 180) * measure.userDepth

    return { height, deltaY }
  }

  /**
   * Shorthand to evaluate current set options for the right sidewall
   * @param sidewall The sidewall context values
   * @param measure The measure context values
   * @returns An object of evaluated options
   */
  public static optionsRight(
    sidewall: ITerraceSidewall,
    measure: ITerraceMeasure
  ) {
    const isGlass = Status.glassSideRight(sidewall)
    const tieNumberTop = LogBar.tieSideRightTop(sidewall)
    const isDoor = Status.sidewallDoorRight(sidewall)
    const isSlanted = Status.sidewallSlantedRight(sidewall)
    const windowsSideTotal = this.windowsSideTotalSpecial(measure, isSlanted)
    const logsSideTotal = this.logsSideTotalSpecial(measure, isSlanted)

    return {
      isGlass,
      tieNumberTop,
      isDoor,
      isSlanted,
      windowsSideTotal,
      logsSideTotal,
    }
  }

  /**
   * Shorthand to evaluate current set options for the left sidewall
   * @param sidewall The sidewall context values
   * @param measure The measure context values
   * @returns An object of evaluated options
   */
  public static optionsLeft(
    sidewall: ITerraceSidewall,
    measure: ITerraceMeasure
  ) {
    const isGlass = Status.glassSideLeft(sidewall)
    const tieNumberTop = LogBar.tieSideLeftTop(sidewall)
    const isDoor = Status.sidewallDoorLeft(sidewall)
    const isSlanted = Status.sidewallSlantedLeft(sidewall)
    const windowsSideTotal = this.windowsSideTotalSpecial(measure, isSlanted)
    const logsSideTotal = this.logsSideTotalSpecial(measure, isSlanted)

    return {
      isGlass,
      tieNumberTop,
      isDoor,
      isSlanted,
      windowsSideTotal,
      logsSideTotal,
    }
  }

  /**
   * Calculates the slanted height (option: schraeg) of the ties for sidewall
   * @param index The index of the tie
   * @param measure The measure context values
   * @param isSlanted If the option schraeg is selected
   * @param deltaY The vertical offset values that shapes the sidewall top window
   * @returns The slanted height if the option schraeg is selected
   */
  public static tieHeightSlanted(
    index: number,
    measure: ITerraceMeasure,
    isSlanted: boolean,
    deltaY: number
  ) {
    const windowsSideTotal = this.windowsSideTotalSpecial(measure, isSlanted)
    const logSideTotal = this.logsSideTotalSpecial(measure, isSlanted)

    if (!isSlanted) return 0

    console.log(logSideTotal)

    return (
      Math.tan((measure.userAngle * Math.PI) / 180) *
        (measure.userDepth / windowsSideTotal) *
        Math.abs(index - logSideTotal) +
      deltaY
    )
  }

  /**
   * Calculates the moon roof position of a front window
   * @param index The index of the window
   * @param measure The measure context values
   * @returns The moon roof position of a front window
   */
  public static moonPosition(index: number, measure: ITerraceMeasure): number {
    const windowsFrontTotal = measure.windowsFrontTotal
    const normalIndex = Math.floor(measure.windowsFrontTotal / 2)
    const isEven = windowsFrontTotal % 2 === 0
    const endPosition = 2 * cover.bar.depth
    const distributionZ = this.glassDistributionFront(measure)

    if (index === 0 || index === windowsFrontTotal - 1) return endPosition

    if (!isEven) {
      return (
        this.moonPositionOdd(
          index,
          normalIndex,
          endPosition,
          distributionZ,
          windowsFrontTotal
        ) ?? 0
      )
    }

    if (isEven) {
      return (
        this.moonPositionEven(
          index,
          normalIndex,
          endPosition,
          distributionZ,
          windowsFrontTotal
        ) ?? 0
      )
    }

    return 0
  }

  /**
   * When the option schraeg is set, a different number of windows is required
   * @param measure The measure context values
   * @param isSlanted If the option schraeg is selected
   * @returns The total number of windows if slanted or not
   */
  public static windowsSideTotalSpecial(
    measure: ITerraceMeasure,
    isSlanted: boolean
  ) {
    return measure.userDepth > 400 && isSlanted ? 4 : measure.windowsSideTotal
  }

  /**
   * When the option schraeg is set, a different width of windows is required
   * @param measure The measure context values
   * @param isSlanted If the option schraeg is selected
   * @returns The width of a side window if slanted or not
   */
  public static windowsSideWidthSpecial(
    measure: ITerraceMeasure,
    isSlanted: boolean
  ) {
    return measure.userDepth > 400 && isSlanted
      ? (measure.windowsSideWidth / 4) * Math.floor(measure.userDepth / 100)
      : measure.windowsSideWidth
  }

  /**
   * When the option schrag is set, a different number of logs are needed
   * @param measure The measure context values
   * @param isSlanted If the option schraeg is selected
   * @returns The total number of logs if slanted or not
   */
  public static logsSideTotalSpecial(
    measure: ITerraceMeasure,
    isSlanted: boolean
  ) {
    switch (true) {
      case measure.userDepth < 400 && isSlanted:
        return 2

      case measure.userDepth >= 400 && isSlanted:
        return 3
      default:
        return measure.logsSideTotal
    }
  }

  /**
   * Calculates the offset for a moon window (Helper)
   * @param measure The measure context values
   * @returns The position of a moon window
   */
  public static glassDistributionFront(measure: ITerraceMeasure) {
    const outerWindows = 2

    let distribution = 0.2

    switch (true) {
      case measure.userWidth >= 600 && measure.userWidth < 800:
        distribution *= 2
        break
      case measure.userWidth >= 800 && measure.userWidth < 1000:
        distribution *= 4
        break
      case measure.userWidth >= 1000:
        distribution *= 3
        break
      default:
        distribution = 0.2
    }

    return Math.floor(measure.windowsFrontTotal - outerWindows) / distribution
  }

  /**
   * Calculates the position for a moon window when the width is odd
   * @param index The index of the moon window
   * @param normalIndex The index where symmetry ends
   * @param endPosition The start position for the outer moon windows
   * @param distributionZ The offset between each moon window
   * @param windowsFrontTotal The total number of moon windows
   * @returns The postion of a moon window
   */
  public static moonPositionOdd(
    index: number,
    normalIndex: number,
    endPosition: number,
    distributionZ: number,
    windowsFrontTotal: number
  ) {
    if (index <= normalIndex) {
      return endPosition - (index * cover.bar.depth) / distributionZ
    }

    if (index > normalIndex) {
      return (
        endPosition -
        (Math.abs(index + 1 - windowsFrontTotal) * cover.bar.depth) /
          distributionZ
      )
    }
  }

  /**
   * Calculates the position for a moon window when the width is even
   * @param index The index of the moon window
   * @param normalIndex The index where symmetry ends
   * @param endPosition The start position for the outer moon windows
   * @param distributionZ The offset between each moon window
   * @param windowsFrontTotal The total number of moon windows
   * @returns The postion of a moon window
   */
  public static moonPositionEven(
    index: number,
    normalIndex: number,
    endPosition: number,
    distributionZ: number,
    windowsFrontTotal: number
  ) {
    if (index === normalIndex - 1) {
      return endPosition - ((index + 1) * cover.bar.depth) / distributionZ
    }

    if (index <= normalIndex) {
      return endPosition - (index * cover.bar.depth) / distributionZ
    }

    if (index > normalIndex) {
      return (
        endPosition -
        (Math.abs(index + 1 - windowsFrontTotal) * cover.bar.depth) /
          distributionZ
      )
    }
  }

  /**
   * Tells the color and opacity for both: vsGlass and dockTile options
   * @param measure The measure context values
   * @param roof The roof context values
   * @returns The color and opacity for vsGlass and dockTile options
   */
  public static colorAndOpacity(measure: ITerraceMeasure, roof: ITerraceRoof) {
    const isVSGlass = Status.vsGlass(measure)

    const glassQuality = {
      color: color.white,
      opacity: cover.terraceCover.safetyGlass as TGlassOpacity | TDockOpacity,
    }

    if (isVSGlass) {
      glassQuality.color =
        roof.safetyGlass === 1.2 ? color.sunblock : color.white
      glassQuality.opacity = roof.safetyGlass === 1.2 ? 0.5 : roof.safetyGlass
    }

    if (!isVSGlass) {
      glassQuality.color =
        roof.dockGlass === 0.35 ? color.dockGreen : color.white
      glassQuality.opacity = roof.dockGlass
    }

    return { color: glassQuality.color, opacity: glassQuality.opacity }
  }
}
